import { heatCell, maturityTierLabel, MATURITY_RAMP, MATURITY_TIER_LABELS } from './color.ts';

// A colour-graded grid of dimension scores (0..5). Presentational and generic over
// { name, value } so the respondent results screen and, later, the admin analysis
// views can share it. Each tile is self-describing (name + number + level) so the
// heatmap is never colour-alone — the colour is a redundant, at-a-glance channel.
export interface HeatmapItem {
  name: string;
  value: number | null;
}

function fmt(n: number | null): string {
  return n === null || Number.isNaN(n) ? '—' : n.toFixed(1);
}

export function DimensionHeatmap({ items }: { items: HeatmapItem[] }) {
  return (
    <div>
      <ul
        className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-5"
        aria-label="Your maturity score for each dimension"
      >
        {items.map((it) => {
          const style = heatCell(it.value);
          const tier = maturityTierLabel(it.value);
          return (
            <li
              key={it.name}
              className="flex min-h-[92px] flex-col justify-between rounded-xl p-3 ring-1 ring-black/10"
              style={style}
              aria-label={`${it.name}: ${fmt(it.value)} out of 5, ${tier}`}
            >
              <span className="text-xs font-medium leading-snug">{it.name}</span>
              <span className="mt-2 flex items-baseline gap-1.5">
                <span className="text-2xl font-bold tabular-nums">{fmt(it.value)}</span>
                <span className="text-[11px] font-medium opacity-80">{tier}</span>
              </span>
            </li>
          );
        })}
      </ul>

      {/* Legend: colour → maturity level, so the ramp is explicit (never colour-alone). */}
      <ul className="mt-4 flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-ink-soft">
        {MATURITY_TIER_LABELS.map((label, i) => (
          <li key={label} className="flex items-center gap-1.5">
            <span
              className="inline-block h-2.5 w-2.5 rounded-sm ring-1 ring-black/10"
              style={{ background: MATURITY_RAMP[i] }}
              aria-hidden
            />
            <span>
              {i + 1} · {label}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
